import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { auth } from "../services/auth";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { addPost, updatePost, getPost } from "../services/blog";
import Loader from "../shared/Loader";
import { Helmet } from "react-helmet";

function CreateArea({ onAdd, post, onEdit }) {
	const [content, setContent] = useState("");
	const [image, setImage] = useState(null);
	const [keywords, setKeywords] = useState("");
	const [name, setName] = useState(post ? post.name : "");
	const [error, setError] = useState("");
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmiting, setIsSubmiting] = useState(false);
	const [config, setConfig] = useState(null);
	
	const navigate = useNavigate();

	const user = auth().getUser();
	const isAdmin = auth().isAdmin();

	const { id } = useParams();

	useEffect(() => {
		if (isAdmin) {
			setConfig({
				headers: { Authorization: `Bearer ${auth().getToken()}` },
			});
		} else {
			navigate("/");
		}
	}, []);

	const fetchPost = useCallback(() => {
		if (config && id) {
			setIsLoading(true);
			getPost(id, config)
				.then((response) => {
					if (response.data) {
						setName(response.data.name);
						setContent(response.data.description);
						setImage(response.data.image);
						setHasError(false);
					} else {
						setHasError(true);
					}
				})
				.catch((er) => {
					setHasError(true);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [id, config]);

	useEffect(() => {
		fetchPost();
	}, [fetchPost]);

	const handleError = (error) => {
		if (error && error.response) {
			switch (error.response.status) {
				case 400:
					if (error.response.data.message === "Invalid blog Id") {
						setError("Invalid blog Id");
					} else {
						setError("Fill all requred fields.");
					}
					break;
				case 500:
					setError("Submit failed, please try again.");
					break;
				default:
					setError("Something went wrong. Please check your internet conection and try again.");
					break;
			}
		}
	};

	const handleFileInputChange = (event) => {
		const file = event.target.files[0];
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => {
			setImage(reader.result);
		};
	};

	const addBlog = () => {
		setIsSubmiting(true);
		addPost(user.id, { content, image, keywords, name, description: content }, config)
			.then((response) => {
				setError("");
				navigate(`/blog/view/${response.data.id}`);
			})
			.catch((error) => {
				handleError(error);
			})
			.finally(() => {
				setIsSubmiting(false);
			});
	};

	const editBlog = () => {
		setIsSubmiting(true);
		updatePost(id, { content, image, keywords, name, description: content }, config)
			.then((response) => {
				setError("");
				setIsSubmiting(false);
				navigate(`/blog/view/${id}`);
			})
			.catch((error) => {
				handleError(error);
			})
			.finally(() => {
				setIsSubmiting(false);
			});
	};

	const removeImage = () => {
		setImage(null);
	};

	const handlePost = (event) => {
		event.preventDefault();
		if (!name || !content) {
			return;
		}
		if (post) {
			if (onEdit) {
				onEdit({
					name,
					content: content,
					image,
					id: post.id,
				});
			}
		} else {
			if (onAdd) {
				onAdd({
					name,
					content: content,
					image,
					id: 10,
				});
			}
		}
	};
	return (
		<>
			<Helmet>
				<title>Dailysocialboost | {id ? "Edit" : "Add"} post</title>
			</Helmet>
			{isLoading ? (
				<div className="flex flex-row self-center">
					<Loader styles={"w-20 h-20 self-center"}></Loader>{" "}
				</div>
			) : (
				<>
					{hasError ? (
						<>
							<p className="text-3xl font-bold my-8 mx-auto text-red-600 text-center">
								Post was not found!
							</p>
							<Link
								to={`/blog`}
								className="bg-blue-500 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded transition-all"
							>
								Return to blog
							</Link>
						</>
					) : (
						<form onSubmit={handlePost}>
							<div className="mt-2">
								<p>
									<label className="uppercase font-semibold" htmlFor="name">
										Title{" "}
										<span title="this field is required" className="font-normal text-red-600">
											*
										</span>
									</label>
								</p>
								<input
									// maxLength="50"
									name="title"
									value={name}
									onChange={(e) => setName(e.target.value)}
									className={`${!name ? "border-2 border-rose-500" : "border-transparent border-b-gray-350"
										} h-8 w-full bg-white outline-0 border`}
									type="text"
									required
								/>
								{!name ? <p className="text-red-600">This field is required!</p> : null}
							</div>
							<div className="flex flex-col justify-center my-3">
								<div className="flex-none">
									<label className="uppercase font-semibold" htmlFor="message">
										Post{" "}
										<span title="this field is required" className="font-normal text-red-600">
											*
										</span>
									</label>
								</div>
								<div>
									<ReactQuill
										value={content}
										onChange={(e) => {
											if (e === "<p><br></p>") {
												setContent("");
											} else {
												setContent(e);
											}
										}}
										modules={{
											toolbar: [
												[{ header: [1, 2, 3, false] }],
												["bold", "italic", "underline", "strike"],
												[{ align: [] }],
												[{ list: "ordered" }, { list: "bullet" }],
												["link", "image"],
												["clean"],
											],
										}}
									/>
									{!content ? <p className="text-red-600">This field is required!</p> : null}
								</div>
							</div>
							<div>
								<label
									className="block mb-2 text-gray-900 uppercase font-semibold"
									htmlFor="file_input"
								>
									Image
								</label>
							</div>
							<div>
								<div>
									<input
										className=" w-full rounded-lg border-slate-800 ring-2 ring-pink-100"
										accept="image/*"
										type="file"
										onChange={handleFileInputChange}
									/>
									{image && <img className="h-52 mt-5 " src={image} alt="file" />}
									{image && (
										<div>
											<button
												onClick={removeImage}
												className="bg-black hover:bg-gray-700 text-white font-bold py-2 px-4 rounded mt-5"
											>
												Remove Image
											</button>
										</div>
									)}
								</div>
							</div>
							{error ? <p className="text-red-600 mt-5 font-bold">{error}</p> : null}
							<div>
								<button
									disabled={!name || !content || isSubmiting}
									onClick={() => (!id ? addBlog() : editBlog())}
									className="bg-pink-500 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded mt-5"
								>
									Submit
								</button>
							</div>
						</form>
					)}
				</>
			)}
		</>
	);
}

export default CreateArea;
